import { Link, useLocation, useRouter } from '@tanstack/react-router'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import type { CopyId } from '@npc/copy'
import type { Schemas } from '@npc/api-client'
import { copyText } from '@npc/platform'

import { daysBetween, hhmm } from '../../app/format'
import type { ActivityTime } from '../../app/activity-query'
import { useMobileNav } from '../../app/mobile-nav'
import { useAcknowledgeAlert, useAlerts, useFollowActivity, useMarkActivityRead, useProjects } from '../../app/queries'
import { cn } from '../../components/cn'
import { t } from '../../copy'
import { T } from '../../copy/text'
import { ScreenLoading } from '../../components/state'
import { activityListBody, followingEmptyCopy } from './empty'
import type { ActivityItem } from './merge'
import { displayStatus, streamSeqToMarkRead, unreadAfterCursor } from './merge'
import { activityHref, parseActivitySearch, type ActivitySearch, type ActivityView } from './search'
import { statusText, statusTone, type StatusTone } from './status'
import { isTailField, truncateTail } from './truncate'

let preservedListScroll = 0

const TYPE_OPTIONS = [
  { id: 'task', copy: 'activity.type.task' as const },
  { id: 'conversation', copy: 'activity.type.conversation' as const },
  { id: 'project', copy: 'activity.type.project' as const },
  { id: 'release', copy: 'activity.type.release' as const },
  { id: 'site', copy: 'activity.type.site' as const },
  { id: 'resource', copy: 'activity.type.resource' as const },
  { id: 'team', copy: 'activity.type.team' as const },
] as const

const STATUS_OPTIONS = [
  'queued',
  'running',
  'awaiting_decision',
  'awaiting_dependency',
  'platform_held',
  'disconnected',
  'recovering',
  'completed',
  'production_verified',
  'failed',
  'rolled_back',
  'unknown',
] as const

// 色值住在 activity.css 的 [data-ac] 色板里（与这一屏其余暗色同一处登记），
// 这里只按人挑一档。用在 inline style 的 background 上，var() 直接生效。
const AVATAR: Record<string, string> = {
  柯: 'var(--av-blue)',
  码: 'var(--av-green)',
  译: 'var(--av-amber)',
  光: 'var(--av-violet)',
  你: 'var(--av-grey)',
  Lu: 'var(--av-blue)',
  系: 'var(--av-grey)',
  玖: 'var(--av-rust)',
}

type Props = {
  items: ActivityItem[]
  excludedItems?: ActivityItem[]
  search: ActivitySearch
  lockedProjectId?: string
  projectName?: string
  actionCount: number
  followCount: number
  omitted: number
  disconnected: boolean
  lastUpdatedAt?: string
  recoveredCount?: number
  recoveredDedupe?: number
  resetNotice?: boolean
  listFailed?: boolean
  macos?: boolean
  lastReadStreamSeq?: number
  watermark?: number
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
  onRetry?: () => void
}

export function ActivityView(props: Props) {
  const {
    items,
    excludedItems = [],
    search,
    lockedProjectId,
    projectName,
    actionCount,
    followCount,
    omitted,
    disconnected,
    lastUpdatedAt,
    recoveredCount,
    recoveredDedupe,
    resetNotice,
    listFailed,
    macos,
    lastReadStreamSeq = 0,
    watermark = 0,
    hasMore = false,
    loadingMore = false,
    onLoadMore,
    onRetry,
  } = props
  const location = useLocation()
  const router = useRouter()
  const [filtersOpen, setFiltersOpen] = useState(search.frame === 'M04' || search.frame === 'A06')
  const listRef = useRef<HTMLElement | null>(null)
  const listBody = activityListBody({ listFailed, disconnected, itemCount: items.length })
  const selected = items.find((item) => item.id === search.activity_id)
  const mobileDetail = Boolean(search.activity_id)
  const catalog = search.frame === 'A11'
  const states = search.frame === 'A09'
  // 真实态按已读游标数未读；设计稿演示态按条目自带的 unread 标记数。
  // 原来 A01/M01 两档直接写死 12，那个数不来自任何东西。
  const unread = search.frame
    ? items.filter((item) => item.safe_variables?.unread === '1').length
    : unreadAfterCursor(items, lastReadStreamSeq)
  const readSeq = watermark > 0 ? watermark : items.reduce((max, item) => Math.max(max, item.stream_seq), 0)
  const markRead = useMarkActivityRead()
  useEffect(() => {
    if (search.frame) return
    const seq = streamSeqToMarkRead(selected, lastReadStreamSeq)
    if (seq == null) return
    markRead.mutate(seq)
  }, [search.frame, selected?.id, selected?.stream_seq, lastReadStreamSeq])

  function replace(next: ActivitySearch) {
    const href = activityHref(location.pathname, next, lockedProjectId)
    router.history.push(href)
  }

  useLayoutEffect(() => {
    const el = listRef.current
    if (el) el.scrollTop = preservedListScroll
  })

  return (
    <div
      data-ac=""
      data-ac-vp={search.frame?.startsWith('M') ? 'mobile' : 'desk'}
      data-testid={lockedProjectId ? 'project-module-activity' : 'activity-shell'}
      data-frame={search.frame ?? ''}
      className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
    >
      {macos ? <MacTitlebar /> : null}
      <MobileChrome
        disconnected={disconnected}
        lastUpdatedAt={lastUpdatedAt}
        unread={unread}
        mobileDetail={mobileDetail}
        selected={selected}
        onClose={() => replace({ ...search, activity_id: undefined })}
        onOpenFilters={() => setFiltersOpen(true)}
      />
      {mobileDetail ? null : (
        <div className="border-b border-[var(--l1)] bg-[var(--bg-1)] px-3 min-[601px]:hidden">
          <ViewTabs
            view={search.view}
            actionCount={actionCount}
            followCount={followCount}
            omitTestids
            onChange={(view) => replace({ ...search, view, activity_id: undefined })}
          />
          <div className="flex flex-wrap py-2">
            <OpsKindBar
              omitTestids
              alerts={Boolean(search.alerts)}
              onChange={(alerts) => replace({ ...search, alerts: alerts || undefined })}
            />
          </div>
        </div>
      )}

      {disconnected ? (
        <div data-testid="activity-disconnected" className="shrink-0 border-b border-[var(--wn)] bg-[var(--wn-s)] px-[18px] py-1.5 text-npc-caption font-medium text-[var(--wn-t)] max-[600px]:px-[13px]">
          <T id="activity.stream.disconnected" vars={{ time: lastUpdatedAt ? hhmm(lastUpdatedAt) : hhmm(new Date().toISOString()) }} />
        </div>
      ) : null}

      <div className="flex min-h-0 min-w-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-[var(--bg-0)]">
          <PageHead
            search={search}
            lockedProjectId={lockedProjectId}
            projectName={projectName}
            actionCount={actionCount}
            followCount={followCount}
            unread={unread}
            omitted={omitted}
            disconnected={disconnected}
            lastUpdatedAt={lastUpdatedAt}
            recoveredCount={recoveredCount}
            recoveredDedupe={recoveredDedupe}
            resetNotice={resetNotice}
            readSeq={readSeq}
            onToggleFilters={() => setFiltersOpen((open) => !open)}
            onChange={(next) => replace({ ...search, ...next })}
          />

          <section
            ref={listRef}
            data-testid="activity-list"
            onScroll={(event) => {
              preservedListScroll = event.currentTarget.scrollTop
            }}
            className={cn(
              'flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto px-[18px] py-3 max-[600px]:px-[13px] max-[600px]:py-[11px]',
              'min-[1280px]:min-w-[640px]',
              mobileDetail && 'max-[600px]:hidden',
            )}
          >
            {states ? (
              <StatusTable />
            ) : catalog ? (
              <EmptyCatalog />
            ) : search.alerts ? (
              <OpsAlertInbox />
            ) : listBody === 'list_failed' ? (
              <Empty
                titleId="activity.empty.list_failed"
                descId="activity.empty.list_failed.hint"
                action={
                  <GhostButton onClick={onRetry}>
                    <T id="activity.empty.retry" />
                  </GhostButton>
                }
              />
            ) : listBody === 'waiting' ? (
              <div data-testid="activity-waiting" className="min-h-[160px]">
                <ScreenLoading shape="timeline" width="reading" />
              </div>
            ) : listBody === 'empty' ? (
              <EmptyMatrix search={search} followCount={followCount} onChange={(next) => replace({ ...search, ...next })} />
            ) : (
              <>
                {search.frame === 'A07' || search.frame === 'M05' ? <OfflineNote /> : null}
                {search.frame === 'A08' ? <RecoverNote /> : null}
                <GroupedList
                  items={items}
                  search={search}
                  disconnected={disconnected}
                  lockedProjectId={lockedProjectId}
                />
                {search.view === 'following' ? <FollowFoot /> : null}
                {search.view === 'following' ? <FollowPanels /> : null}
                {search.view === 'action' && (excludedItems.length > 0 || search.frame === 'A02' || search.frame === 'M02') ? (
                  <ExcludedList items={excludedItems} />
                ) : null}
                {search.view === 'all' && (search.frame === 'A01' || !search.frame) ? <InternalNote /> : null}
                {search.frame === 'A05' ? <ProjectNote /> : null}
                {search.frame === 'A06' ? <SearchPanels /> : null}
                {search.frame === 'A12' ? <ShaNote /> : null}
                {search.frame === 'A16' && search.view === 'all' ? <HeldFailPanels /> : null}
                {hasMore ? (
                  <button
                    type="button"
                    data-testid="activity-load-more"
                    disabled={loadingMore}
                    onClick={onLoadMore}
                    className="mx-auto mt-3 min-h-11 rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-1)] px-4 text-npc-body font-medium text-[var(--t2)] hover:border-[var(--l3)] hover:text-[var(--t1)] disabled:cursor-wait disabled:opacity-60"
                  >
                    <T id={loadingMore ? 'activity.list.loading_more' : 'activity.list.load_more'} />
                  </button>
                ) : null}
              </>
            )}
          </section>
        </div>

        {selected ? (
          <DetailSidecar
            item={selected}
            disconnected={disconnected}
            mobile={mobileDetail}
            macos={macos}
            frame={search.frame}
            onClose={() => replace({ ...search, activity_id: undefined })}
          />
        ) : null}
      </div>

      {filtersOpen ? (
        <FilterSheet
          search={search}
          lockedProjectId={lockedProjectId}
          onClose={() => setFiltersOpen(false)}
          onChange={(next) => replace({ ...search, ...next })}
        />
      ) : null}

      {search.view === 'action' && search.frame === 'M02' ? (
        <div className="hidden shrink-0 border-t border-[var(--l1)] bg-[var(--bg-1)] px-3 py-2.5 pb-[22px] text-npc-body-sm leading-[1.65] text-[var(--t3)] max-[600px]:block">
          <T id="activity.excluded.hint" />
        </div>
      ) : null}
    </div>
  )
}

function MacTitlebar() {
  return (
    <div
      data-testid="activity-macos-titlebar"
      className="hidden h-[30px] shrink-0 items-center gap-2 border-b border-[var(--l1)] bg-[var(--bg-2)] px-3 min-[601px]:flex"
    >
      <span className="size-[11px] rounded-full bg-[var(--mac-close)]" />
      <span className="size-[11px] rounded-full bg-[var(--mac-min)]" />
      <span className="size-[11px] rounded-full bg-[var(--mac-zoom)]" />
      <span className="flex-1" />
      <span className="text-npc-caption text-[var(--t3)]">
        <T id="activity.macos.title" />
      </span>
      <span className="flex-1" />
    </div>
  )
}

function MobileChrome({
  disconnected,
  lastUpdatedAt,
  unread,
  mobileDetail,
  selected,
  onClose,
  onOpenFilters,
}: {
  disconnected: boolean
  lastUpdatedAt?: string
  unread: number
  mobileDetail: boolean
  selected?: ActivityItem
  onClose: () => void
  onOpenFilters: () => void
}) {
  const nav = useMobileNav()
  const title = mobileDetail && selected ? crumbTitle(selected) : t('activity.page.title')
  const sub = mobileDetail && selected
    ? `${statusText(displayStatus(selected, disconnected), selected.rollback_label)} · ${hhmm(selected.occurred_at)}`
    : disconnected
      ? t('activity.stream.disconnected', { time: lastUpdatedAt ? hhmm(lastUpdatedAt) : hhmm(new Date().toISOString()) })
      : unread > 0
        ? `${t('activity.conn.live')} · ${t('activity.unread.count', { count: String(unread) })}`
        : t('activity.conn.live')
  return (
    <header
      data-testid="mobile-header"
      className="flex h-[60px] shrink-0 items-center gap-2.5 border-b border-[var(--l1)] bg-[var(--bg-1)] px-3 min-[601px]:hidden"
    >
      <button
        type="button"
        title={mobileDetail ? t('activity.detail.close') : t('ui.navigation')}
        aria-label={mobileDetail ? t('activity.detail.close') : t('ui.navigation')}
        onClick={mobileDetail ? onClose : () => nav.open()}
        className="flex size-11 shrink-0 items-center justify-center rounded-npc-code border border-[var(--l2)] bg-[var(--bg-2)] text-[var(--t2)]"
      >
        {mobileDetail ? (
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        ) : (
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12h18" />
            <path d="M3 6h18" />
            <path d="M3 18h18" />
          </svg>
        )}
      </button>
      <div className="min-w-0 flex-1">
        <div className="truncate text-npc-section text-[var(--t1)]">{title}</div>
        <div
          className={cn('truncate text-npc-caption', disconnected || (mobileDetail && selected && displayStatus(selected, disconnected) === 'unknown') ? 'text-[var(--wn-t)]' : 'text-[var(--ok-t)]')}
        >
          {sub}
        </div>
      </div>
      {mobileDetail ? (
        <button type="button" title={t('activity.detail.follow')} className="h-11 shrink-0 rounded-lg border border-[var(--l2)] bg-[var(--bg-2)] px-3.5 text-npc-body text-[var(--t2)]">
          <T id="activity.detail.follow" />
        </button>
      ) : (
        <button
          type="button"
          data-testid="activity-filter-toggle"
          title={t('activity.filter.open')}
          onClick={onOpenFilters}
          className="h-11 shrink-0 rounded-lg border border-[var(--l2)] bg-[var(--bg-2)] px-3.5 text-npc-body text-[var(--t2)]"
        >
          <T id="activity.filter.open" />
        </button>
      )}
    </header>
  )
}

function PageHead({
  search,
  lockedProjectId,
  projectName,
  actionCount,
  followCount,
  unread,
  omitted,
  disconnected,
  lastUpdatedAt,
  recoveredCount,
  recoveredDedupe,
  resetNotice,
  readSeq,
  onToggleFilters,
  onChange,
}: {
  search: ActivitySearch
  lockedProjectId?: string
  projectName?: string
  actionCount: number
  followCount: number
  unread: number
  omitted: number
  disconnected: boolean
  lastUpdatedAt?: string
  recoveredCount?: number
  recoveredDedupe?: number
  resetNotice?: boolean
  readSeq: number
  onToggleFilters: () => void
  onChange: (patch: Partial<ActivitySearch>) => void
}) {
  const read = useMarkActivityRead()
  const filtered = Boolean(search.q || search.type || search.status || search.actor || search.time || (!lockedProjectId && (search.project_id || search.scope)))
  const subtitle =
    search.frame === 'A13'
      ? t('activity.page.subtitle.narrow')
      : search.frame === 'A09'
      ? t('activity.states.subtitle')
      : lockedProjectId
        ? t('activity.page.subtitle.project')
        : search.view === 'action'
          ? t('activity.view.action.subtitle')
          : search.view === 'important'
            ? t('activity.view.important.subtitle')
          : search.view === 'following'
            ? t('activity.view.following.subtitle')
            : t('activity.page.subtitle')

  return (
    <div className="hidden shrink-0 border-b border-[var(--l1)] bg-[var(--bg-1)] px-[18px] py-3.5 min-[601px]:block">
      <div className="mb-1.5 flex flex-wrap items-baseline gap-2.5">
        <h1 className="font-npc-display text-[20px] font-bold tracking-[-0.01em] text-[var(--t1)]">{/* npc-token-allow: 动态中心 v2.1 设计稿（design/Activity.dc.html）自带的 20px 标题字阶，整站 token 无此档 */}
          {search.frame === 'A09' ? (
            <T id="activity.states.title" />
          ) : projectName ? (
            <T id="activity.page.title.project" vars={{ project: projectName }} />
          ) : (
            <T id="activity.page.title" />
          )}
        </h1>
        {disconnected ? (
          <span className="rounded-md bg-[var(--wn-s)] px-2 py-0.5 text-npc-caption font-medium text-[var(--wn-t)]">
            <T id="activity.stream.disconnected" vars={{ time: lastUpdatedAt ? hhmm(lastUpdatedAt) : hhmm(new Date().toISOString()) }} />
          </span>
        ) : null}
        {recoveredCount && recoveredCount > 0 ? (
          <span data-testid="activity-recovered" className="rounded-md bg-[var(--ok-s)] px-2 py-0.5 text-npc-caption font-medium text-[var(--ok-t)]">
            <T id="activity.stream.recovered" vars={{ count: String(recoveredCount) }} />
          </span>
        ) : null}
        <span className="flex-1" />
        {filtered ? (
          <HeadButton testid="activity-filter-clear" onClick={() => onChange({ q: undefined, type: undefined, status: undefined, actor: undefined, time: undefined, project_id: undefined, scope: undefined })}>
            <T id="activity.filter.clear" />
          </HeadButton>
        ) : (
          <HeadButton
            testid="activity-mark-all-read"
            onClick={() => {
              if (readSeq > 0) read.mutate(readSeq)
            }}
          >
            <T id="activity.action.mark_all_read" />
          </HeadButton>
        )}
        {filtered ? (
          <HeadButton
            onClick={() => {
              // 走接缝：`copyText` 不抛，剪贴板不存在时回 unsupported 而不是
              // 一条没人接的 rejected promise。这一屏没有失败态的文案
              // （`activity.action.copy_link` 只有一句「复制链接」），
              // 所以失败时界面照旧不变——**但不再有静默的未处理异常**。
              // 要给他一句话，得先有一条 docs/copy 模板，归流 0。
              void copyText(window.location.href)
            }}
          >
            <T id="activity.action.copy_link" />
          </HeadButton>
        ) : null}
        {search.view === 'following' ? (
          <HeadButton>
            <T id="activity.action.manage_follow" />
          </HeadButton>
        ) : null}
      </div>
      <p className="max-w-[880px] text-npc-section-sm leading-[1.6] text-[var(--t3)]">{subtitle}</p>
      <OpsKindBar alerts={Boolean(search.alerts)} onChange={(alerts) => onChange({ alerts: alerts || undefined })} />
      {recoveredDedupe && recoveredDedupe > 0 ? (
        <p data-testid="activity-deduped" className="mt-1 text-npc-caption text-[var(--t3)]">
          <T id="activity.stream.recovered.deduped" vars={{ count: String(recoveredDedupe) }} />
        </p>
      ) : null}
      {resetNotice ? (
        <p data-testid="activity-stream-reset" className="mt-1 text-npc-caption text-[var(--t3)]">
          <T id="activity.stream.reset" />
        </p>
      ) : null}
      {omitted > 0 ? (
        <p data-testid="activity-omitted" className="mt-1 text-npc-caption text-[var(--t3)]">
          <T id="activity.omitted" vars={{ count: String(omitted) }} />
        </p>
      ) : null}

      {lockedProjectId || (search.frame && search.frame !== 'A01' && search.frame !== 'M01') ? null : (
        <ScopeSelector search={search} onChange={onChange} />
      )}

      <ViewTabs
        view={search.view}
        actionCount={actionCount}
        followCount={followCount}
        onChange={(view) => onChange({ view, activity_id: undefined })}
      />

      <FilterBar
        search={search}
        lockedProjectId={lockedProjectId}
        disconnected={disconnected}
        unread={unread}
        onToggle={onToggleFilters}
        onChange={onChange}
      />
    </div>
  )
}

function HeadButton({ children, onClick, testid }: { children: ReactNode; onClick?: () => void; testid?: string }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="h-8 rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-1)] px-3.5 text-npc-body text-[var(--t2)]"
    >
      {children}
    </button>
  )
}

function ViewTabs({
  view,
  actionCount,
  followCount,
  omitTestids,
  onChange,
}: {
  view: ActivityView
  actionCount: number
  followCount: number
  omitTestids?: boolean
  onChange: (view: ActivityView) => void
}) {
  const tabs: { id: ActivityView; copy: 'activity.view.all' | 'activity.view.important' | 'activity.view.action' | 'activity.view.following'; count: number; tone: 'w' | 'n' | '' }[] = [
    { id: 'action', copy: 'activity.view.action', count: actionCount, tone: 'w' },
    { id: 'important', copy: 'activity.view.important', count: 0, tone: '' },
    { id: 'all', copy: 'activity.view.all', count: 0, tone: '' },
    { id: 'following', copy: 'activity.view.following', count: followCount, tone: 'n' },
  ]
  return (
    <div role="tablist" data-testid="activity-view-tabs" className="-mb-px mt-2.5 flex flex-wrap items-center gap-0.5 border-b border-[var(--l1)] max-[600px]:mt-0">
      {tabs.map((tab) => {
        const active = view === tab.id
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={omitTestids ? undefined : `activity-view-${tab.id}`}
            onClick={() => onChange(tab.id)}
            className={cn(
              'relative flex h-[34px] items-center gap-1.5 px-3 text-npc-section-sm',
              active ? 'ac-tab-active' : 'font-normal text-[var(--t3)]',
            )}
          >
            <T id={tab.copy} />
            {tab.count > 0 ? (
              <span
                data-testid={!omitTestids && tab.id === 'action' ? 'activity-action-count' : undefined}
                className={cn(
                  'flex h-[19px] min-w-[19px] items-center justify-center rounded-full px-1.5 font-npc-mono text-npc-caption font-semibold',
                  tab.tone === 'w'
                    ? 'bg-[var(--wn-s)] text-[var(--wn-t)]'
                    : active || tab.id === 'following'
                      ? 'ac-count-accent'
                      : 'bg-[var(--bg-3)] text-[var(--t3)]',
                )}
              >
                {tab.count}
              </span>
            ) : null}
            {active ? <span className="ac-tab-underline absolute inset-x-0 bottom-0" /> : null}
          </button>
        )
      })}
    </div>
  )
}

function ScopeSelector({
  search,
  onChange,
}: {
  search: ActivitySearch
  onChange: (patch: Partial<ActivitySearch>) => void
}) {
  const projects = useProjects()
  const value = search.scope === 'platform' ? 'platform' : search.project_id ? `project:${search.project_id}` : 'all'
  return (
    <label data-testid="activity-scope" className="mt-2.5 flex w-fit max-w-full items-center gap-2 text-npc-caption text-[var(--t4)]">
      <span className="shrink-0">
        <T id="activity.scope.label" />
      </span>
      <select
        aria-label={t('activity.scope.label')}
        value={value}
        onChange={(event) => {
          const next = event.target.value
          if (next === 'platform') {
            onChange({ scope: 'platform', project_id: undefined })
            return
          }
          if (next.startsWith('project:')) {
            onChange({ scope: undefined, project_id: next.slice('project:'.length) })
            return
          }
          onChange({ scope: undefined, project_id: undefined })
        }}
        className="h-8 max-w-[300px] truncate rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-2)] px-2.5 text-npc-body-sm font-medium text-[var(--t1)] outline-none"
      >
        <option value="all">{t('activity.scope.all')}</option>
        <option value="platform">{t('activity.scope.platform')}</option>
        {(projects.data?.items ?? []).map((project: Schemas['Project']) => (
          <option key={project.id} value={`project:${project.id}`}>{project.name}</option>
        ))}
      </select>
    </label>
  )
}

function FilterBar({
  search,
  lockedProjectId,
  disconnected,
  unread,
  onToggle,
  onChange,
}: {
  search: ActivitySearch
  lockedProjectId?: string
  disconnected: boolean
  unread: number
  onToggle: () => void
  onChange: (patch: Partial<ActivitySearch>) => void
}) {
  const chips: { key: string; label: string; on: boolean }[] =
    search.frame === 'A06' || search.frame === 'A15'
      ? [
          { key: 'project', label: t('activity.project.name.npcshop'), on: true },
          { key: 'type', label: search.frame === 'A15' ? t('activity.type.release') : `${t('activity.type.release')} · ${t('activity.type.site')}`, on: true },
          { key: 'status', label: `${t('activity.status.failed')} · ${t('activity.status.rolled_back')}`, on: true },
          { key: 'time', label: t('activity.filter.time.week'), on: true },
        ]
      : [
          { key: 'type', label: search.type ? t((TYPE_OPTIONS.find((o) => o.id === search.type)?.copy ?? 'activity.filter.type') as 'activity.filter.type') : t('activity.filter.type'), on: Boolean(search.type) },
          { key: 'status', label: search.status ? statusText(search.status as ActivityItem['status']) : t('activity.filter.status'), on: Boolean(search.status) },
          { key: 'actor', label: search.actor || t('activity.filter.actor'), on: Boolean(search.actor) },
          { key: 'time', label: search.time ? t(`activity.filter.time.${search.time}` as 'activity.filter.time.today') : t('activity.filter.time'), on: Boolean(search.time) },
        ]
  return (
    <div data-testid="activity-filter-bar" className="mt-2.5 flex flex-wrap items-center gap-1.5">
      <label className="flex h-[29px] min-w-0 flex-[0_1_240px] items-center gap-2 rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-2)] px-2.5">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-[var(--t4)]">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          type="search"
          value={search.q ?? ''}
          onChange={(event) => onChange({ q: event.target.value || undefined })}
          placeholder={disconnected ? t('activity.filter.search.disconnected') : lockedProjectId ? t('activity.filter.search.project') : t('activity.filter.search')}
          className="min-w-0 flex-1 border-none bg-transparent p-0 text-npc-body-sm text-[var(--t1)] outline-none placeholder:text-[var(--t4)]"
        />
      </label>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          data-testid={`activity-filter-${chip.key}`}
          onClick={onToggle}
          className={cn(
            'flex h-[29px] items-center gap-1.5 rounded-full border px-2.5 text-npc-body-sm',
            chip.on
              ? 'ac-chip-active'
              : 'border-[var(--l2)] bg-[var(--bg-2)] text-[var(--t2)]',
          )}
        >
          {chip.label}
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="opacity-50">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      ))}
      <span className="flex-1" />
      {search.frame === 'A06' ? (
        <span className="font-npc-mono text-npc-caption text-[var(--t4)]">
          <T id="activity.filter.hint.results" vars={{ count: '3' }} />
        </span>
      ) : lockedProjectId ? (
        <span className="font-npc-mono text-npc-caption text-[var(--t4)]">
          <T id="activity.filter.hint.project" />
        </span>
      ) : unread > 0 && (!search.frame || search.frame === 'A01' || search.frame === 'M01') ? (
        <span className="font-npc-mono text-npc-caption text-[var(--t4)]">
          <T id="activity.unread.count" vars={{ count: String(unread) }} />
        </span>
      ) : null}
    </div>
  )
}

function GroupedList({
  items,
  search,
  disconnected,
  lockedProjectId,
}: {
  items: ActivityItem[]
  search: ActivitySearch
  disconnected: boolean
  lockedProjectId?: string
}) {
  const location = useLocation()
  const groups = useMemo(() => groupByDay(items, search.frame), [items, search.frame])
  return (
    <div className="flex flex-col gap-2">
      {groups.map((group) => (
        <div key={group.id}>
          <div className="mb-2 flex items-center gap-2 px-0.5">
            <span className="font-npc-display text-npc-caption font-bold tracking-[0.06em] text-[var(--t4)]">{group.label}</span>
            <div className="h-px flex-1 bg-[var(--l1)]" />
            {group.meta ? (
              <span className="font-npc-mono text-npc-caption text-[var(--t4)]">{group.meta}</span>
            ) : null}
          </div>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {group.items.map((item) => (
              <li key={item.event_id}>
                <ThreadCard
                  item={item}
                  disconnected={disconnected}
                  selected={item.id === search.activity_id}
                  href={activityHref(location.pathname, { ...search, activity_id: item.id }, lockedProjectId)}
                  expandSteps={search.frame === 'A02' || search.frame === 'A12' || search.frame === 'A16' || search.frame === 'A17'}
                  hideProject={Boolean(lockedProjectId)}
                  showTails={search.frame === 'A12' || search.frame === 'A17'}
                  showGate={search.frame === 'A02' || search.frame === 'M02'}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

function groupByDay(items: ActivityItem[], frame?: string): { id: string; label: string; items: ActivityItem[]; meta?: string }[] {
  if (frame === 'A02') {
    return [{ id: 'decide', label: t('activity.group.decide', { count: String(items.length) }), items }]
  }
  if (frame === 'A06') {
    return [{ id: 'search', label: t('activity.search.group'), items, meta: t('activity.search.meta') }]
  }
  if (frame === 'A07' || frame === 'M05') {
    return [{ id: 'offline', label: t('activity.group.offline'), items }]
  }
  if (frame === 'A08') {
    return [{ id: 'recovered', label: t('activity.group.recovered'), items, meta: t('activity.group.recovered.meta') }]
  }
  if (frame === 'A12') {
    return [{ id: 'sha', label: t('activity.group.unclosed'), items }]
  }
  if (frame === 'A17') {
    const single = items.filter((item) => item.event_id !== 'task-long-1')
    const long = items.filter((item) => item.event_id === 'task-long-1')
    const out: { id: string; label: string; items: ActivityItem[]; meta?: string }[] = []
    if (single.length) out.push({ id: 'single', label: t('activity.group.single'), items: single, meta: t('activity.group.events', { count: String(single.length) }) })
    if (long.length) out.push({ id: 'long', label: t('activity.group.long'), items: long })
    return out
  }
  const buckets = new Map<string, ActivityItem[]>()
  for (const item of items) {
    const days = daysBetween(item.occurred_at)
    const id = days <= 0 ? 'today' : days === 1 ? 'yesterday' : 'earlier'
    const list = buckets.get(id) ?? []
    list.push(item)
    buckets.set(id, list)
  }
  const order = ['today', 'yesterday', 'earlier']
  return order
    .filter((id) => buckets.has(id))
    .map((id) => ({
      id,
      label: t(
        id === 'today'
          ? 'activity.filter.time.today'
          : id === 'yesterday'
            ? 'activity.filter.time.yesterday'
            : 'activity.filter.time.earlier',
      ),
      items: buckets.get(id) ?? [],
      meta: id === 'today' && frame === 'A05'
        ? t('activity.group.locked')
        : id === 'today' && (frame === 'A01' || !frame)
          ? t('activity.group.threads', { count: String(buckets.get(id)?.length ?? 0) })
          : undefined,
    }))
}

function ThreadCard({
  item,
  disconnected,
  selected,
  href,
  expandSteps,
  hideProject,
  showTails,
  showGate,
}: {
  item: ActivityItem
  disconnected: boolean
  selected: boolean
  href: string
  expandSteps?: boolean
  hideProject?: boolean
  showTails?: boolean
  showGate?: boolean
}) {
  const router = useRouter()
  const status = displayStatus(item, disconnected)
  const tone = statusTone(status)
  const unread = item.safe_variables?.unread === '1'
  const role = item.safe_variables?.role
  const result = item.safe_variables?.result
  const threadCount = Number(item.safe_variables?.thread_count ?? '1')
  const crumbs = crumbList(item, hideProject)
  const accent = item.safe_variables?.accent || (needsAccent(item, status) ? statusTone(status) : '')
  const emphasized = Boolean(accent)
  const showResult = Boolean(result && (item.requires_action || ['awaiting_decision', 'platform_held', 'failed', 'rolled_back', 'unknown'].includes(status)))
  const stages = (item.stages ?? []).filter((stage) => stage.keep || keepStage(stage.state))
  const showSteps = Boolean(expandSteps && stages.length > 0)
  const av = item.safe_variables?.av || item.actor_display.slice(0, 1)
  return (
    <a
      href={href}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
        event.preventDefault()
        router.history.push(href)
      }}
      data-testid={`activity-thread-${item.id}`}
      className={cn(
        'ac-feed-card relative block overflow-hidden border bg-[var(--bg-1)] no-underline',
        emphasized ? 'border-[var(--l3)]' : 'border-[var(--l2)]',
      )}
      style={{
        background: selected ? 'var(--bg-3)' : highlightBg(item, status, item.safe_variables?.hi),
      }}
    >
      {emphasized ? <span className="absolute inset-y-0 left-0 w-0.5" style={{ background: accentColor((accent as StatusTone) || tone) }} /> : null}
      <div className="flex items-start gap-2.5 px-3 py-2.5 max-[600px]:px-2.5 max-[600px]:py-2.5">
        <span
          className="mt-0 flex size-[29px] shrink-0 items-center justify-center rounded-npc-tag-sm text-npc-caption font-semibold text-white"
          style={{ background: AVATAR[av] ?? 'var(--bg-4)' }}
        >
          {av}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-1.5">
            <span className="text-npc-body font-semibold text-[var(--t1)]">{item.actor_display}</span>
            {role ? <span className="text-npc-caption text-[var(--t4)]">{role}</span> : null}
            <span className="flex-1" />
            <time className="shrink-0 font-npc-mono text-npc-caption text-[var(--t4)]" dateTime={item.occurred_at}>
              {item.safe_variables?.time || (daysBetween(item.occurred_at) === 1 ? t('activity.filter.time.yesterday') : hhmm(item.occurred_at))}
            </time>
          </div>
          <p data-testid="activity-summary" className="mt-1 whitespace-normal break-words text-npc-section-sm leading-[1.45] text-[var(--t1)]">
            {item.summary}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <StatusPill status={status} rollbackLabel={item.rollback_label} />
            {crumbs.map((c, i) => (
              <span key={`${c}-${i}`} className="max-w-[220px] truncate text-npc-caption text-[var(--t4)]">
                {i ? ` › ${c}` : c}
              </span>
            ))}
            {item.unclosed ? (
              <span data-testid="activity-unclosed" className="text-npc-caption text-[var(--t4)]">
                <T id="activity.unclosed" />
              </span>
            ) : null}
            {threadCount > 1 ? (
              <span className="text-npc-caption text-[var(--t4)]">
                <T id="activity.thread.updates" vars={{ count: String(threadCount) }} />
              </span>
            ) : null}

          </div>
          {showGate && item.action_category ? (
            <p data-testid={`activity-gate-${item.action_category}`} className="mt-1 text-npc-caption text-[var(--t3)]">
              <T id={gateCopy(item.action_category)} />
            </p>
          ) : null}
          {showResult ? (
            <div
              className="mt-2 rounded-npc-tag px-2.5 py-2 text-npc-body-sm leading-[1.6]"
              style={{ background: resultBg(item, status, item.safe_variables?.resultTone), color: resultColor(item, status, item.safe_variables?.resultTone) }}
            >
              {result}
            </div>
          ) : null}
          {status === 'platform_held' ? (
            <>
              <p className="mt-2 text-npc-caption text-[var(--t3)]">
                <T id="activity.status.platform_held.hint" />
              </p>
              <p className="mt-1 text-npc-caption text-[var(--t3)]">
                <T id="activity.status.platform_held.no_resubmit" />
              </p>
            </>
          ) : null}
          {status === 'unknown' ? (
            <p className="mt-2 text-npc-caption text-[var(--t3)]">
              <T id="activity.status.unknown.hint" />
            </p>
          ) : null}
          {showSteps ? (
            <div className="mt-2 grid gap-1.5 border-t border-[var(--l1)] pt-2">
              {stages.map((stage, index) => (
                <div key={`${stage.label}-${index}`} className="flex items-start gap-2">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full" style={{ background: accentColor(statusTone((stage.state as ActivityItem['status']) || 'unknown')) }} />
                  <span className="w-24 shrink-0 text-npc-caption text-[var(--t3)]">{stage.label}</span>
                  <span className="min-w-0 flex-1 break-words text-npc-caption text-[var(--t3)]">{stage.desc}</span>
                </div>
              ))}
            </div>
          ) : null}
          {showTails ? <EvidenceTails refs={item.evidence_refs} /> : null}
          {item.safe_variables?.primary || item.safe_variables?.secondary ? (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {item.safe_variables?.primary ? (
                <span className="inline-flex h-[30px] items-center rounded-npc-tag border border-[var(--pri)] bg-[var(--pri)] px-3 text-npc-body-sm font-semibold text-[var(--pri-t)]">
                  <T id={actionCopy(item.safe_variables.primary)} />
                </span>
              ) : null}
              {item.safe_variables?.secondary ? (
                <span className="inline-flex h-[30px] items-center rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-1)] px-3 text-npc-body-sm text-[var(--t2)]">
                  <T id={actionCopy(item.safe_variables.secondary)} />
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        {unread ? (
          <span data-testid="activity-unread" className="mt-1.5 size-[7px] shrink-0 rounded-full bg-[var(--wn)]">
            <span className="sr-only">
              <T id="activity.unread" />
            </span>
          </span>
        ) : null}
      </div>
      <span className="sr-only">{tone}</span>
    </a>
  )
}

function EvidenceTails({ refs }: { refs?: ActivityItem['evidence_refs'] }) {
  const tails = (refs ?? []).filter((ref) => isTailField(ref.kind) && ref.value)
  if (tails.length === 0) return null
  return (
    <ul className="mt-2 m-0 flex list-none flex-col gap-1 p-0">
      {tails.map((ref, index) => (
        <li
          key={`${ref.kind}-${index}`}
          data-testid="activity-tail-field"
          className="overflow-hidden text-ellipsis whitespace-nowrap font-npc-mono text-npc-caption text-[var(--t3)]"
          title={ref.value}
        >
          {ref.label ? `${ref.label} · ` : ''}
          {truncateTail(ref.value ?? '', 12)}
        </li>
      ))}
    </ul>
  )
}

function StatusPill({ status, rollbackLabel }: { status: ActivityItem['status']; rollbackLabel?: string }) {
  const tone = statusTone(status)
  return (
    <span
      data-status={status}
      className="inline-flex min-h-[22px] items-center rounded-md px-2 py-0.5 text-npc-caption font-medium whitespace-nowrap"
      style={pillStyle(tone)}
    >
      {statusText(status, rollbackLabel)}
    </span>
  )
}

function DetailSidecar({
  item,
  disconnected,
  mobile,
  macos,
  frame,
  onClose,
}: {
  item: ActivityItem
  disconnected: boolean
  mobile: boolean
  macos?: boolean
  frame?: ActivitySearch['frame']
  onClose: () => void
}) {
  const follow = useFollowActivity()
  const [evidenceOpen, setEvidenceOpen] = useState(false)
  const status = displayStatus(item, disconnected)
  const stages = item.stages ?? []
  const kept = stages.filter((stage) => stage.keep || keepStage(stage.state))
  const folded = Math.max(0, stages.length - kept.length)

  return (
    <aside
      data-testid="activity-sidecar"
      className={cn(
        'flex min-h-0 shrink-0 flex-col overflow-y-auto border-l border-[var(--l2)] bg-[var(--bg-1)]',
        frame === 'A13'
          ? 'w-[360px] min-w-[360px] max-w-[360px]'
          : macos
            ? 'w-[400px]'
            : 'w-[360px] min-[1280px]:w-[420px] min-[1280px]:min-w-[380px] min-[1280px]:max-w-[480px]',
        mobile && 'max-[600px]:fixed max-[600px]:inset-0 max-[600px]:z-40 max-[600px]:w-full max-[600px]:border-l-0',
      )}
    >
      <div className="shrink-0 border-b border-[var(--l1)] px-3.5 py-3">
        <div className="mb-1 flex items-center gap-2">
          <h2 className="font-npc-display text-npc-section font-bold text-[var(--t1)]">{crumbTitle(item)}</h2>
          <span className="flex-1" />
          <SidecarFollowControl item={item} mutate={follow.mutate} />
          <button
            type="button"
            title={t('activity.detail.close')}
            className="flex size-[26px] items-center justify-center rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-2)] text-[var(--t3)]"
            onClick={onClose}
          >
            <span className="sr-only">
              <T id="activity.detail.close" />
            </span>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <p className="text-npc-body-sm leading-[1.65] text-[var(--t3)]">
          <T id={frame === 'A13' ? 'activity.narrow.sidecar_sub' : 'activity.detail.sidecar_sub'} />
        </p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-3.5 py-3">
        {frame === 'A13' ? <NarrowOrder /> : null}
        {/*
          CouponSidecar 整块是**设计稿演示内容**：'已上线'、'v128 · f18c04b'、
          '小柯领取'、'SHA 一致'、'平台已验证' 全是写死的。
          原来它的条件只看条目名字叫不叫 '结算页优惠券'——**那条路在生产上是通的**，
          真有一条同名的动态就会被这块假的顶掉整个详情（结果、关键节点、证据都没了），
          而它还会对着用户说「平台已验证」。
          现在加上 frame：frame 在生产构建里恒为 undefined，这一支只在设计稿演示态里走得到。
        */}
        {frame && frame !== 'A13' && item.safe_variables?.object === '结算页优惠券' ? (
          <CouponSidecar macos={macos} />
        ) : (
          <>
            <SideSection label={t('activity.detail.result')}>
              <p className="whitespace-normal break-words text-npc-body font-semibold text-[var(--t1)]">{item.summary}</p>
              <div className="mt-2">
                <StatusPill status={status} rollbackLabel={item.rollback_label} />
              </div>
              {item.unclosed ? (
                <p className="mt-2 text-npc-caption text-[var(--t3)]">
                  <T id="activity.unclosed.hint" />
                </p>
              ) : null}
              {status === 'platform_held' ? (
                <p className="mt-2 text-npc-caption text-[var(--t3)]">
                  <T id="activity.status.platform_held.hint" />
                </p>
              ) : null}
            </SideSection>
            <SideSection label={t('activity.detail.stages')}>
              {folded > 0 ? (
                <p className="mb-2 text-npc-caption text-[var(--t3)]">
                  <T id="activity.stages.folded" vars={{ count: String(folded) }} />
                </p>
              ) : null}
              <ol className="m-0 flex list-none flex-col gap-2 p-0">
                {kept.map((stage, index) => (
                  <li key={`${stage.label}-${index}`} className="text-npc-body text-[var(--t1)]">
                    <div className="font-medium">{stage.label}</div>
                    {stage.desc ? <div className="mt-1 text-npc-caption text-[var(--t3)]">{stage.desc}</div> : null}
                  </li>
                ))}
              </ol>
            </SideSection>
            <SideSection label={t('activity.detail.evidence')}>
              <button type="button" className="min-h-11 text-npc-body font-semibold text-[var(--t1)]" onClick={() => setEvidenceOpen((open) => !open)}>
                <T id="activity.detail.view_evidence" />
              </button>
              {evidenceOpen ? (
                <ul className="mt-2 m-0 flex list-none flex-col gap-1 p-0 text-npc-caption text-[var(--t3)]">
                  {(item.evidence_refs ?? []).map((ref, index) => (
                    <li key={`${ref.kind}-${index}`} className="overflow-hidden text-ellipsis whitespace-nowrap" title={ref.value}>
                      {ref.label || ref.kind}
                      {ref.value ? ` · ${isTailField(ref.kind) ? truncateTail(ref.value, 12) : ref.value}` : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <button type="button" className="mt-1 block min-h-11 text-npc-caption text-[var(--t3)]" onClick={() => setEvidenceOpen(true)}>
                  <T id="activity.detail.view_evidence" />
                </button>
              )}
            </SideSection>
          </>
        )}
      </div>
      <div className="shrink-0 border-t border-[var(--l1)] bg-[var(--bg-2)] px-3.5 py-2.5">
        <p className="mb-2.5 text-npc-caption leading-[1.65] text-[var(--t3)]">
          <T id="activity.detail.boundary" />
        </p>
        <div className="flex flex-wrap gap-1.5">
          <Link to={item.source_url || '/chat'} className="inline-flex h-[31px] items-center rounded-npc-tag border border-[var(--pri)] bg-[var(--pri)] px-3 text-npc-body-sm font-semibold text-[var(--pri-t)] no-underline">
            <T id="activity.detail.open_session" />
          </Link>
          <span className="inline-flex h-[31px] items-center rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-1)] px-3 text-npc-body-sm text-[var(--t2)]">
            {macos ? <T id="activity.detail.view_evidence" /> : <T id="activity.detail.go_release" />}
          </span>
        </div>
      </div>
    </aside>
  )
}

function SideSection({ label, note, children }: { label: string; note?: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="font-npc-display text-npc-caption font-bold tracking-[0.06em] text-[var(--t4)]">{label}</span>
        <div className="h-px flex-1 bg-[var(--l1)]" />
      </div>
      <div className="overflow-hidden rounded-lg border border-[var(--l2)] bg-[var(--bg-2)]">{children}</div>
      {note ? <p className="mt-1.5 text-npc-caption leading-[1.65] text-[var(--t3)]">{note}</p> : null}
    </div>
  )
}

function FilterSheet({
  search,
  lockedProjectId,
  onClose,
  onChange,
}: {
  search: ActivitySearch
  lockedProjectId?: string
  onClose: () => void
  onChange: (patch: Partial<ActivitySearch>) => void
}) {
  const projects = useProjects()
  return (
    <div
      data-testid="activity-filter-sheet"
      className="fixed inset-0 z-30 flex flex-col justify-end bg-black/50 min-[601px]:absolute min-[601px]:inset-auto min-[601px]:right-4 min-[601px]:top-[220px] min-[601px]:w-[360px] min-[601px]:bg-transparent"
    >
      <div className="flex max-h-[78%] flex-col overflow-hidden rounded-t-[14px] border-t border-[var(--l2)] bg-[var(--bg-1)] min-[601px]:max-h-none min-[601px]:rounded-lg min-[601px]:border min-[601px]:border-[var(--l2)] min-[601px]:p-3 min-[601px]:shadow-[0_14px_38px_rgba(0,0,0,.62)]">
        <div className="min-[601px]:hidden">
            <div className="flex justify-center pt-2.5">
              <span className="h-1 w-[34px] rounded-sm bg-[var(--l3)]" />
            </div>
            <div className="border-b border-[var(--l1)] px-[15px] py-2.5">
              <div className="text-npc-modal-title font-bold text-[var(--t1)]">
                <T id="activity.filter.open" />
              </div>
              <div className="mt-1 text-npc-body-sm leading-[1.6] text-[var(--t3)]">
                <T id="activity.sheet.sub" />
              </div>
            </div>
        </div>
        <div className="flex flex-col gap-2.5 overflow-y-auto px-[15px] py-2.5 min-[601px]:px-0 min-[601px]:py-0">
          {lockedProjectId ? null : (
            <SheetGroup label={t('activity.scope.label')}>
              <SheetRow
                active={!search.project_id && search.scope !== 'platform'}
                label={t('activity.scope.all')}
                badge={t('activity.scope.all')}
                onClick={() => onChange({ project_id: undefined, scope: undefined })}
              />
              <SheetRow
                active={search.scope === 'platform'}
                label={t('activity.scope.platform')}
                onClick={() => onChange({ project_id: undefined, scope: 'platform' })}
              />
              {(projects.data?.items ?? []).map((project: Schemas['Project']) => (
                <SheetRow
                  key={project.id}
                  active={search.project_id === project.id}
                  label={project.name}
                  onClick={() => onChange({ project_id: project.id, scope: undefined })}
                />
              ))}
            </SheetGroup>
          )}
          <SheetGroup label={t('activity.filter.status')}>
            {STATUS_OPTIONS.map((id) => (
              <SheetRow
                key={id}
                active={search.status === id}
                label={statusText(id)}
                onClick={() => onChange({ status: search.status === id ? undefined : id })}
              />
            ))}
          </SheetGroup>
          <ChipSelect
            label={t('activity.filter.type')}
            value={search.type}
            options={TYPE_OPTIONS.map((opt) => ({ id: opt.id, label: t(opt.copy) }))}
            onChange={(type) => onChange({ type })}
          />
          <ChipSelect
            label={t('activity.filter.time')}
            value={search.time}
            options={[
              { id: 'today', label: t('activity.filter.time.today') },
              { id: 'yesterday', label: t('activity.filter.time.yesterday') },
              { id: 'earlier', label: t('activity.filter.time.earlier') },
            ]}
            onChange={(time) => onChange({ time: time as ActivityTime | undefined })}
          />
          <label className="flex min-h-11 flex-col gap-1 text-npc-caption text-[var(--t3)]">
            <T id="activity.filter.actor" />
            <input
              value={search.actor ?? ''}
              onChange={(event) => onChange({ actor: event.target.value || undefined })}
              className="min-h-11 rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-0)] px-2 text-npc-body text-[var(--t1)]"
            />
          </label>
        </div>
        <div className="flex gap-2 border-t border-[var(--l1)] px-[15px] py-2.5 pb-[22px] min-[601px]:hidden">
            <button type="button" className="h-11 flex-1 rounded-npc-button border border-[var(--l2)] bg-[var(--bg-1)] text-npc-body-lg text-[var(--t2)]" onClick={() => onChange({ q: undefined, type: undefined, status: undefined, actor: undefined, time: undefined, project_id: undefined, scope: undefined })}>
              <T id={search.frame === 'M04' ? 'activity.sheet.clear' : 'activity.filter.clear'} />
            </button>
            <button type="button" className="h-11 flex-1 rounded-npc-button border border-[var(--pri)] bg-[var(--pri)] text-npc-body-lg font-semibold text-[var(--pri-t)]" onClick={onClose}>
              <T id="activity.sheet.apply" />
            </button>
          </div>
      </div>
    </div>
  )
}

function SheetGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2 font-npc-display text-npc-caption font-bold tracking-[0.06em] text-[var(--t4)]">{label}</div>
      <div className="overflow-hidden rounded-npc-code border border-[var(--l2)] bg-[var(--bg-2)]">{children}</div>
    </div>
  )
}

function SheetRow({
  label,
  badge,
  sub,
  active,
  danger,
  warn,
  onClick,
}: {
  label: string
  badge?: string
  sub?: string
  active?: boolean
  danger?: boolean
  warn?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[46px] w-full items-center gap-2.5 border-b border-[var(--l1)] px-3 py-2.5 text-left"
      style={{ background: active ? 'var(--ok-s)' : 'transparent' }}
    >
      {active ? <span className="size-2 shrink-0 rounded-full bg-[var(--ok)]" /> : danger ? <span className="size-2 shrink-0 rounded-full bg-[var(--bad)]" /> : warn ? <span className="size-2 shrink-0 rounded-full bg-[var(--wn)]" /> : null}
      <span className="min-w-0 flex-1">
        <span className="block text-npc-body-xl text-[var(--t1)]">{label}</span>
        {sub ? <span className="block text-npc-caption text-[var(--t4)]">{sub}</span> : null}
      </span>
      {badge ? <span className="rounded-md bg-[var(--ok-s)] px-2 py-0.5 text-npc-caption text-[var(--ok-t)]">{badge}</span> : null}
    </button>
  )
}

function ChipSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value?: string
  options: { id: string; label: string }[]
  onChange: (value?: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-npc-caption text-[var(--t3)]">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => {
          const active = value === opt.id
          return (
            <button
              key={opt.id}
              type="button"
              data-testid={`activity-filter-option-${opt.id}`}
              onClick={() => onChange(active ? undefined : opt.id)}
              className={cn(
                'min-h-11 min-w-11 rounded-npc-tag px-3 text-npc-caption',
                active ? 'border border-[var(--l3)] bg-[var(--bg-4)] text-[var(--t1)]' : 'text-[var(--t3)]',
              )}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ExcludedList({ items: _items }: { items: ActivityItem[] }) {
  return (
    <section data-testid="activity-excluded" className="mt-3 rounded-lg border border-[var(--l2)] bg-[var(--bg-1)] px-[13px] py-[11px]">
      <div className="mb-1 flex items-center gap-2">
        <span className="size-[7px] rounded-full bg-[var(--t4)]" />
        <h2 className="text-npc-section-sm font-semibold text-[var(--t1)]">
          <T id="activity.excluded.title" />
        </h2>
      </div>
      <p className="text-npc-body leading-[1.7] text-[var(--t2)]">
        <T id="activity.excluded.hint" />
      </p>
      <div className="mt-2 grid gap-px">
        <div className="flex gap-2.5 border-b border-[var(--l1)] py-1">
          <span className="w-[104px] shrink-0 text-npc-caption text-[var(--t4)]">
            <T id="activity.excluded.rollback" />
          </span>
          <span className="min-w-0 flex-1 text-npc-body-sm text-[var(--ok-t)]">
            <T id="activity.excluded.rollback.desc" />
          </span>
        </div>
        <div className="flex gap-2.5 border-b border-[var(--l1)] py-1">
          <span className="w-[104px] shrink-0 text-npc-caption text-[var(--t4)]">
            <T id="activity.excluded.held" />
          </span>
          <span className="min-w-0 flex-1 text-npc-body-sm text-[var(--ok-t)]">
            <T id="activity.excluded.held.desc" />
          </span>
        </div>
        <div className="flex gap-2.5 border-b border-[var(--l1)] py-1">
          <span className="w-[104px] shrink-0 text-npc-caption text-[var(--t4)]">
            <T id="activity.excluded.retry" />
          </span>
          <span className="min-w-0 flex-1 text-npc-body-sm text-[var(--ok-t)]">
            <T id="activity.excluded.retry.desc" />
          </span>
        </div>
      </div>
    </section>
  )
}

function InternalNote() {
  return (
    <div className="mt-3 rounded-lg border border-[var(--l2)] bg-[var(--bg-1)] px-[13px] py-[11px]">
      <div className="mb-1 flex items-center gap-2">
        <span className="size-[7px] rounded-full bg-[var(--t4)]" />
        <span className="text-npc-section-sm font-semibold text-[var(--t1)]">
          <T id="activity.internal.title" />
        </span>
      </div>
      <p className="text-npc-body leading-[1.7] text-[var(--t2)]">
        <T id="activity.internal.desc" />
      </p>
    </div>
  )
}

function OpsKindBar({
  alerts,
  omitTestids,
  onChange,
}: {
  alerts: boolean
  omitTestids?: boolean
  onChange: (alerts: boolean) => void
}) {
  const query = useAlerts('open')
  const openCount = query.data?.items?.length ?? 0
  return (
    <div data-testid={omitTestids ? undefined : 'activity-kind-bar'} className="mt-2 flex flex-wrap items-center gap-1.5 max-[600px]:mt-0">
      <button
        type="button"
        aria-pressed={!alerts}
        data-testid={omitTestids ? undefined : 'activity-kind-activity'}
        onClick={() => onChange(false)}
        className={cn(
          'min-h-11 min-w-11 rounded-md px-2.5 text-npc-caption',
          !alerts ? 'bg-[var(--bg-3)] font-medium text-[var(--t1)]' : 'bg-[var(--bg-2)] text-[var(--t3)]',
        )}
      >
        <T id="activity.kind.activity" />
      </button>
      <button
        type="button"
        aria-pressed={alerts}
        data-testid={omitTestids ? undefined : 'activity-ops-alerts-chip'}
        onClick={() => onChange(true)}
        className={cn(
          'min-h-11 min-w-11 rounded-md px-2.5 text-npc-caption',
          alerts ? 'bg-[var(--wn-s)] font-medium text-[var(--wn-t)]' : 'bg-[var(--bg-2)] text-[var(--t3)]',
        )}
      >
        <T id="activity.kind.ops_alert" />
        {openCount > 0 ? ` · ${openCount}` : null}
      </button>
    </div>
  )
}

function SidecarFollowControl({
  item,
  mutate,
}: {
  item: ActivityItem
  mutate: ReturnType<typeof useFollowActivity>['mutate']
}) {
  // **拿不到真状态就别假装知道。** 后端只有 POST/DELETE 两个写入口，
  // 没有「这一条我关注了吗」可问，也没有随条目下发的标记。
  // 原来这里读的是夹具里那份写死的关注集合——在真数据上它永远答「没关注」，
  // 而那是一句用户看得见的假话。
  //
  // 所以初始是 undefined = 不知道：按钮此时只说「关注」（一个动作，不是一句状态），
  // 点它发 follow=true（后端幂等，本来就关注着也不会出事）。
  // 只有在这一次点过之后，我们才真的知道，按钮才敢说「取消关注」。
  const [following, setFollowing] = useState<boolean | undefined>(undefined)
  const known = following === true
  const copyId = known ? 'activity.detail.unfollow' : 'activity.detail.follow'
  return (
    <button
      type="button"
      data-testid={known ? 'activity-unfollow' : 'activity-follow'}
      title={t(copyId)}
      className="flex size-[26px] items-center justify-center rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-2)] text-[var(--wn-t)]"
      onClick={() => {
        const next = !known
        mutate(
          { object_type: followType(item.object_type), object_id: item.object_id, follow: next },
          {
            onSuccess: () => setFollowing(next),
          },
        )
      }}
    >
      <span className="sr-only">
        <T id={copyId} />
      </span>
      <svg width="13" height="13" viewBox="0 0 24 24" fill={known ? 'var(--wn)' : 'none'} stroke="currentColor" strokeWidth="1.8">
        <path d="M12 17.3 6.2 20.5l1.1-6.5-4.7-4.6 6.5-.9L12 2.5l2.9 6 6.5.9-4.7 4.6 1.1 6.5z" />
      </svg>
    </button>
  )
}

/**
 * 关注视图底下那句说明。
 *
 * 这里原来是一张「关注的对象」表，**五行全是夹具**（'NPC 商城'、'sg-order-01'、
 * '小柯'、时间 '09:58'）：真的 items 参数被命名成 `_items` 丢掉了。
 *
 * 后端没有「列出我关注了什么」的读接口——`/activity/follow` 只有写入，
 * `store.Following()` 只用来数个数。整张表拿不到一行真数据。
 * 宁可诚实地少显示，也不能显示编造的记录：表删了，
 * 这一页改成显示**关注对象的真实动态**（上面的 GroupedList），
 * 名册等那个接口到了再说。
 */
function FollowFoot() {
  return (
    <p className="mt-3 rounded-lg border border-[var(--l2)] bg-[var(--bg-2)] px-3.5 py-2.5 text-npc-body-sm leading-[1.65] text-[var(--t3)]">
      <T id="activity.follow.table.foot" />
    </p>
  )
}

function EmptyCatalog() {
  const rows: { scene: CopyId; text: string }[] = [
    { scene: 'activity.catalog.scene.all', text: t('activity.empty.all') },
    { scene: 'activity.catalog.scene.action', text: t('activity.empty.action') },
    { scene: 'activity.catalog.scene.following', text: t('activity.empty.following') },
    { scene: 'activity.catalog.scene.search', text: t('activity.empty.search') },
    { scene: 'activity.catalog.scene.disconnected', text: t('activity.stream.disconnected', { time: '14:03' }) },
    { scene: 'activity.catalog.scene.recovered', text: t('activity.stream.recovered', { count: '7' }) },
    { scene: 'activity.catalog.scene.unknown', text: t('activity.status.unknown.hint') },
    { scene: 'activity.catalog.scene.omitted', text: t('activity.omitted', { count: '3' }) },
  ]
  return (
    <div data-testid="activity-catalog" className="overflow-hidden rounded-lg border border-[var(--l2)] bg-[var(--bg-1)]">
      <div className="border-b border-[var(--l1)] bg-[var(--bg-2)] px-3 py-2.5">
        <h2 className="font-npc-display text-[19px] font-bold text-[var(--t1)]">{/* npc-token-allow: 动态中心 v2.1 设计稿（design/Activity.dc.html）自带的 19px 标题字阶，整站 token 无此档 */}
          <T id="activity.catalog.title" />
        </h2>
        <p className="mt-1 text-npc-body-lg text-[var(--t3)]">
          <T id="activity.catalog.subtitle" />
        </p>
      </div>
      <table className="w-full border-collapse text-left text-npc-body">
        <thead>
          <tr className="bg-[var(--bg-2)] text-npc-caption font-semibold text-[var(--t4)]">
            <th className="px-3 py-2 font-semibold">
              <T id="activity.catalog.scene" />
            </th>
            <th className="px-3 py-2 font-semibold">
              <T id="activity.catalog.copy" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.scene} data-testid={`activity-catalog-${row.scene}`} className="border-t border-[var(--l1)]">
              <td className="px-3 py-2 align-top text-[var(--t1)]">
                <T id={row.scene} />
              </td>
              <td className="px-3 py-2 align-top text-[var(--t2)]">{row.text}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}


function OpsAlertInbox() {
  const query = useAlerts('open')
  const ack = useAcknowledgeAlert()
  const items = query.data?.items ?? []
  if (query.isLoading && !query.data) {
    return <div data-testid="activity-ops-alerts" />
  }
  if (items.length === 0) {
    return (
      <div data-testid="activity-ops-alerts">
        <Empty titleId="activity.empty.ops_alerts.title" descId="activity.empty.ops_alerts" />
      </div>
    )
  }
  return (
    <div data-testid="activity-ops-alerts" className="flex flex-col gap-2">
      {items.map((alert) => (
        <article
          key={alert.id}
          data-testid="activity-ops-alert-row"
          className="relative overflow-hidden rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-1)] px-3 py-2.5"
        >
          <div className="text-npc-body font-semibold text-[var(--t1)]">{alert.title}</div>
          <div className="mt-1 text-npc-caption leading-[1.75] text-[var(--t3)]">{alert.affected}</div>
          <button
            type="button"
            data-testid="activity-ops-alert-acknowledge"
            disabled={ack.isPending}
            onClick={() => {
              void ack.mutateAsync(alert.id)
            }}
            className="mt-2 min-h-11 rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-1)] px-3.5 text-npc-caption text-[var(--t2)]"
          >
            <T id="activity.ops_alert.acknowledge" />
          </button>
        </article>
      ))}
    </div>
  )
}

function EmptyMatrix({
  search,
  followCount,
  onChange,
}: {
  search: ActivitySearch
  followCount: number
  onChange: (patch: Partial<ActivitySearch>) => void
}) {
  const filtered = Boolean(search.q || search.type || search.status || search.actor || search.time)
  if (filtered) {
    return (
      <Empty
        titleId="activity.empty.search.title"
        descId="activity.empty.search.hint"
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <GhostButton onClick={() => onChange({ type: undefined, status: undefined, actor: undefined, time: undefined, project_id: undefined })}>
              <T id="activity.empty.search.clear_filters" />
            </GhostButton>
            <GhostButton onClick={() => onChange({ q: undefined, type: undefined, status: undefined, actor: undefined, time: undefined, project_id: undefined })}>
              <T id="activity.empty.search.clear_all" />
            </GhostButton>
          </div>
        }
      />
    )
  }
  if (search.view === 'action') {
    return <Empty titleId="activity.empty.action.title" descId="activity.empty.action" />
  }
  if (search.view === 'important') {
    return <Empty titleId="activity.empty.important.title" descId="activity.empty.important" />
  }
  if (search.view === 'following') {
    // 「一条都没有」有两种，下一个动作不一样：真的没关注过任何东西，
    // 要去关注点什么；关注了但最近没动静，什么都不用做。
    // following_count 是后端算的真数，两者分得开，就不该合成一句。
    const copy = followingEmptyCopy(followCount)
    return (
      <Empty
        titleId={copy.titleId}
        descId={copy.descId}
        action={
          copy.offerProjects ? (
            <Link to="/projects" className="inline-flex h-[31px] items-center rounded-npc-tag border border-[var(--pri)] bg-[var(--pri)] px-3.5 text-npc-caption font-semibold text-[var(--pri-t)] no-underline">
              <T id="activity.empty.go_projects" />
            </Link>
          ) : undefined
        }
      />
    )
  }
  return (
    <Empty
      titleId="activity.empty.all.title"
      descId="activity.empty.all.hint"
      action={
        <div className="flex flex-wrap justify-center gap-2">
          <Link to="/chat" className="inline-flex h-[31px] items-center rounded-npc-tag border border-[var(--pri)] bg-[var(--pri)] px-3.5 text-npc-caption font-semibold text-[var(--pri-t)] no-underline">
            <T id="activity.empty.go_chat" />
          </Link>
          <Link to="/projects" className="inline-flex h-[31px] items-center rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-1)] px-3.5 text-npc-caption text-[var(--t2)] no-underline">
            <T id="activity.empty.go_projects" />
          </Link>
        </div>
      }
    />
  )
}

function Empty({
  titleId,
  descId,
  action,
}: {
  titleId: CopyId
  descId: CopyId
  action?: ReactNode
}) {
  return (
    <div data-testid="activity-empty" className="flex flex-col items-center px-6 py-10 text-center">
      <div className="mb-[18px] flex size-[54px] items-center justify-center rounded-npc-card-md border border-[var(--l2)] bg-[var(--bg-1)]" aria-hidden>
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-[var(--t3)]">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
      </div>
      <p className="font-npc-display text-[19px] font-bold leading-tight text-[var(--t1)]">{/* npc-token-allow: 动态中心 v2.1 设计稿（design/Activity.dc.html）自带的 19px 标题字阶，整站 token 无此档 */}
        <T id={titleId as 'activity.empty.all.title'} />
      </p>
      <p className="mt-2 max-w-[460px] text-npc-body-lg leading-[1.75] text-[var(--t3)]">
        <T id={descId as 'activity.empty.all.hint'} />
      </p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  )
}

function GhostButton({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick} className="h-[31px] min-h-11 rounded-npc-tag border border-[var(--l2)] bg-[var(--bg-1)] px-3.5 text-npc-caption text-[var(--t2)]">
      {children}
    </button>
  )
}

function keepStage(state: string): boolean {
  return ['failed', 'rolled_back', 'waiting', 'awaiting_decision', 'recovering', 'unknown'].includes(state)
}

function followType(objectType: string): 'project' | 'task' | 'site' | 'resource' | 'agent' {
  if (objectType === 'project' || objectType === 'task' || objectType === 'site' || objectType === 'resource' || objectType === 'agent') {
    return objectType
  }
  return 'task'
}

function gateCopy(category: NonNullable<ActivityItem['action_category']>): CopyId {
  switch (category) {
    case 'question':
      return 'activity.gate.question'
    case 'red_confirm':
      return 'activity.gate.red_confirm'
    case 'needs_info':
      return 'activity.gate.needs_info'
    case 'recovery_choice':
      return 'activity.gate.recovery_choice'
    default:
      return 'activity.gate.entitlement'
  }
}

function crumbList(item: ActivityItem, hideProject?: boolean): string[] {
  const crumbs = item.safe_variables?.crumb
    ? item.safe_variables.crumb.split('|').filter(Boolean)
    : [item.safe_variables?.project, item.safe_variables?.object].filter((x): x is string => Boolean(x))
  // 项目页里不重复项目名。原来这里比的是写死的 'NPC 商城'（夹具那个项目），
  // 于是**在真数据上永远不成立**，该藏的那一格从来没藏过。
  // 改成和条目自己带的项目名比：夹具与真数据两边都成立。
  if (hideProject && crumbs.length > 1 && crumbs[0] === item.safe_variables?.project) return crumbs.slice(1)
  return crumbs
}

function crumbTitle(item: ActivityItem): string {
  return item.safe_variables?.object || item.safe_variables?.project || item.summary
}

function needsAccent(item: ActivityItem, status: ActivityItem['status']): boolean {
  return Boolean(item.requires_action || item.unclosed || status === 'failed' || status === 'platform_held' || status === 'unknown' || status === 'production_verified' || status === 'rolled_back')
}

function accentColor(tone: StatusTone): string {
  if (tone === 'g') return 'var(--ok)'
  if (tone === 'r') return 'var(--bad)'
  if (tone === 'w') return 'var(--wn)'
  if (tone === 'a') return 'var(--t3)'
  return 'var(--l2)'
}

function highlightBg(item: ActivityItem, status: ActivityItem['status'], hi?: string): string {
  if (hi === 'r' || (item.requires_action && item.action_category === 'red_confirm')) return 'var(--bad-s)'
  if (hi === 'w' || item.requires_action) return 'var(--wn-s)'
  if (hi === 'a') return 'var(--bg-3)'
  if (status === 'failed') return 'var(--bad-s)'
  if (status === 'unknown' || status === 'platform_held' || item.unclosed) return 'var(--wn-s)'
  return 'var(--bg-1)'
}

function resultBg(item: ActivityItem, status: ActivityItem['status'], tone?: string): string {
  if (tone === 'r' || item.action_category === 'red_confirm' || status === 'failed') return 'var(--bad-s)'
  if (tone === 'g' || status === 'production_verified' || status === 'completed') return 'var(--ok-s)'
  return 'var(--wn-s)'
}

function resultColor(item: ActivityItem, status: ActivityItem['status'], tone?: string): string {
  if (tone === 'r' || item.action_category === 'red_confirm' || status === 'failed') return 'var(--bad-t)'
  if (tone === 'g' || status === 'production_verified' || status === 'completed') return 'var(--ok-t)'
  return 'var(--t2)'
}

function actionCopy(id: string): CopyId {
  switch (id) {
    case 'open_session':
      return 'activity.detail.open_session'
    case 'view_impact':
      return 'activity.detail.view_impact'
    case 'view_evidence':
      return 'activity.detail.view_evidence'
    case 'go_release':
      return 'activity.detail.go_release'
    default:
      return 'activity.detail.continue_lulu'
  }
}

function pillStyle(tone: StatusTone): CSSProperties {
  if (tone === 'g') return { background: 'var(--ok-s)', color: 'var(--ok-t)' }
  if (tone === 'r') return { background: 'var(--bad-s)', color: 'var(--bad-t)' }
  if (tone === 'w') return { background: 'var(--wn-s)', color: 'var(--wn-t)' }
  if (tone === 'a') return { background: 'var(--bg-4)', color: 'var(--t1)' }
  return { background: 'var(--bg-3)', color: 'var(--t3)' }
}


function NarrowOrder() {
  return (
    <SideSection label={t('activity.narrow.order.title')} note={t('activity.narrow.order.note')}>
      <div data-testid="activity-narrow-order">
        <DetailRow title={t('activity.narrow.order.nav')} sub={t('activity.narrow.order.nav.desc')} pill={t('activity.narrow.order.nav.pill')} tone="g" />
        <DetailRow title={t('activity.narrow.order.detail')} sub={t('activity.narrow.order.detail.desc')} pill={t('activity.narrow.order.detail.pill')} tone="g" />
        <DetailRow title={t('activity.narrow.order.list')} sub={t('activity.narrow.order.list.desc')} />
      </div>
    </SideSection>
  )
}

function CouponSidecar({ macos }: { macos?: boolean }) {
  return (
    <>
      <SideSection label={t('activity.detail.result')} note="「已完成」只说明活干完了；「已上线」要同 SHA 部署加验证。">
        <DetailRow title="已上线" sub="真实访问返回 200" time="09:58" pill="已验证" tone="g" />
        <DetailRow title="能退回" sub="v128 · f18c04b" mono />
      </SideSection>
      {macos ? (
        <SideSection label="和 Web 的差别" note="点系统通知仍然回到这同一个动态详情，不新开一套界面。">
          <DetailRow title="多一条标题栏" sub="30px · 红黄绿 + 当前位置" tone="g" />
          <DetailRow title="系统通知" sub="P1 —— 站内动态闭环之后再做" pill="P1" />
          <DetailRow title="业务信息架构" sub="零差异 —— 不为客户端做第二套" pill="一致" tone="g" />
        </SideSection>
      ) : (
        <>
          <SideSection label={t('activity.detail.stages')} note="三个 SHA 一致才算闭环。不一致会拆开标红。">
            <DetailRow title="拆成 4 项 · 小柯领取" sub="三项能并行 · src/pages/checkout" time="09:31" tone="g" />
            <DetailRow title="候选 → 主干 → 部署" sub="三步都是 a3f91c2 · 测试 34 项全过 · 部署 41 秒" time="09:57" pill="SHA 一致" tone="g" mono />
            <DetailRow title="业务验证通过" sub="真实打开页面 + 点开优惠券 + 走完下单" time="09:58" pill="平台已验证" tone="g" />
          </SideSection>
          <SideSection label={t('activity.detail.evidence')} note="技术证据在第二层。">
            <DetailRow title="影响面测试" sub="34 项全过 · 1 分 12 秒" pill="展开" />
            <DetailRow title="部署 + 业务验证收据" sub="verifiedBy=platform · HTTP 200 + 元素断言" pill="展开" />
          </SideSection>
        </>
      )}
    </>
  )
}

function DetailRow({
  title,
  sub,
  time,
  pill,
  tone,
  mono,
}: {
  title: string
  sub?: string
  time?: string
  pill?: string
  tone?: StatusTone
  mono?: boolean
}) {
  return (
    <div className="flex items-start gap-2 border-b border-[var(--l1)] px-2.5 py-2 last:border-b-0">
      {tone ? <span className="mt-1.5 size-[7px] shrink-0 rounded-full" style={{ background: accentColor(tone) }} /> : null}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-1.5">
          <span className={tone === 'g' ? 'text-npc-body font-semibold text-[var(--ok-t)]' : 'text-npc-body font-medium text-[var(--t1)]'}>{title}</span>
          <span className="flex-1" />
          {time ? <span className="font-npc-mono text-npc-caption text-[var(--t4)]">{time}</span> : null}
        </div>
        {sub ? <div className={`mt-1 text-npc-caption text-[var(--t3)] ${mono ? 'font-npc-mono' : ''}`}>{sub}</div> : null}
      </div>
      {pill ? (
        <span className="mt-0.5 shrink-0 rounded-md px-2 py-0.5 text-npc-caption" style={pillStyle(tone === 'g' ? 'g' : 'n')}>
          {pill}
        </span>
      ) : null}
    </div>
  )
}

function StatusTable() {
  const rows: { status: ActivityItem['status']; mean: string; proof: string; wrong: string; rollback?: string; hi?: StatusTone }[] = [
    { status: 'queued', mean: '平台收到了，还没开始', proof: '有入队记录，没有租约', wrong: '—' },
    { status: 'running', mean: '真的在跑', proof: '有当前租约、心跳或持续事件', wrong: '伪造运行', hi: 'a' },
    { status: 'awaiting_decision', mean: '明确需要你做选择', proof: '有 DecisionRequest 且 actor 是你', wrong: '把普通失败塞进来', hi: 'w' },
    { status: 'awaiting_dependency', mean: '等别的任务或资源', proof: 'dependsOn 未满足', wrong: '看起来像卡住了' },
    { status: 'platform_held', mean: '平台故障停了，修好会自动恢复', proof: 'platform_held 事件', wrong: '写成"排队中"' },
    { status: 'disconnected', mean: '客户端确认不了最新状态', proof: 'SSE 断开', wrong: '继续放运行动画' },
    { status: 'recovering', mean: '收到了恢复动作或新租约', proof: '新 lease 或 recover 事件', wrong: '—' },
    { status: 'completed', mean: '活干完了，但不等于上线', proof: 'WorkItem 终态', wrong: '当成已上线' },
    { status: 'production_verified', mean: '同 SHA 部署 + 业务验证都过了', proof: 'verifiedBy=platform 的验证收据', wrong: '只凭部署成功就标', hi: 'g' },
    { status: 'failed', mean: '有稳定的失败结果和原因', proof: '失败事件 + 原因', wrong: '用"系统繁忙"盖住' },
    { status: 'rolled_back', mean: '回到了恢复点', proof: '必须标明是登记还是执行', wrong: '两种混为一谈', rollback: 'both' },
    { status: 'unknown', mean: '证据不够，不猜', proof: '拿不到任何证据', wrong: '填一个看起来合理的', hi: 'w' },
  ]
  return (
    <div data-testid="activity-status-table" className="overflow-hidden rounded-lg border border-[var(--l2)] bg-[var(--bg-1)]">
      <div className="flex items-center gap-2 border-b border-[var(--l1)] bg-[var(--bg-2)] px-3 py-2.5">
        <span className="text-npc-body font-semibold text-[var(--t2)]">
          <T id="activity.states.label" />
        </span>
        <span className="flex-1" />
        <span className="font-npc-mono text-npc-caption text-[var(--t4)]">
          <T id="activity.states.meta" />
        </span>
      </div>
      <div className="grid grid-cols-[112px_minmax(0,1.25fr)_minmax(0,1.35fr)_132px] gap-0 border-b border-[var(--l1)] bg-[var(--bg-2)] px-3 py-2">
        <span className="text-npc-caption font-semibold text-[var(--t4)]"><T id="activity.states.col.status" /></span>
        <span className="text-npc-caption font-semibold text-[var(--t4)]"><T id="activity.states.col.mean" /></span>
        <span className="text-npc-caption font-semibold text-[var(--t4)]"><T id="activity.states.col.proof" /></span>
        <span className="text-npc-caption font-semibold text-[var(--t4)]"><T id="activity.states.col.wrong" /></span>
      </div>
      {rows.map((row) => (
        <div key={row.status} className="grid grid-cols-[112px_minmax(0,1.25fr)_minmax(0,1.35fr)_132px] items-center gap-0 border-b border-[var(--l1)] px-3 py-2" style={{ background: row.hi === 'g' ? 'var(--ok-s)' : row.hi === 'w' ? 'var(--wn-s)' : row.hi === 'a' ? 'var(--bg-3)' : 'transparent' }}>
          {row.rollback === 'both' ? (
            <div className="flex flex-col gap-1">
              <StatusPill status="rolled_back" rollbackLabel={t('activity.status.rollback.registered')} />
              <StatusPill status="rolled_back" rollbackLabel={t('activity.status.rollback.executed')} />
              <span className="text-npc-caption text-[var(--t3)]">{t('activity.status.rolled_back')}</span>
            </div>
          ) : (
            <StatusPill status={row.status} />
          )}
          <span className="pr-2 text-npc-caption text-[var(--t3)]">{row.mean}</span>
          <span className="pr-2 text-npc-caption text-[var(--t3)]">{row.proof}</span>
          <span className={`text-npc-caption ${row.wrong !== '—' ? 'text-[var(--bad-t)]' : 'text-[var(--t4)]'}`}>{row.wrong}</span>
        </div>
      ))}
      <div className="mt-2 px-3 pb-3">
        <span data-status="rolled_back" className="sr-only">{t('activity.status.rollback.executed')}</span>
      </div>
    </div>
  )
}

function FollowPanels() {
  return (
    <div className="mt-3 grid grid-cols-2 gap-2.5">
      <NotePanel title={t('activity.follow.auto.title')} tag={t('activity.follow.auto.tag')} tone="g">
        <Kv k="你参与的项目" v="加入就自动关注" />
        <Kv k="你发起的任务" v="创建时自动关注" />
        <Kv k="你被指派的" v="指派时自动关注" />
        <Kv k="能取消吗" v="能，随时取消，不影响权限" ok />
      </NotePanel>
      <NotePanel title={t('activity.follow.manual.title')} tag={t('activity.follow.manual.tag')}>
        <Kv k="能关注什么" v="项目、任务、站点、资源、Agent 五类" />
        <Kv k="在哪关注" v="动态详情右上角，或者对象自己的页面" />
        <Kv k="关注之后" v="它的变化会进这一页，未读也会算进来" />
        <Kv k="会不会多给权限" v="不会 —— 关注不是授权" ok />
      </NotePanel>
    </div>
  )
}

function ProjectNote() {
  return (
    <div className="mt-3 rounded-lg border border-[var(--l2)] bg-[var(--bg-1)] px-[13px] py-[11px]">
      <div className="mb-1 flex items-center gap-2">
        <span className="size-[7px] rounded-full bg-[var(--t4)]" />
        <span className="text-npc-section-sm font-semibold text-[var(--t1)]">
          <T id="activity.project.note.title" />
        </span>
      </div>
      <p className="text-npc-body leading-[1.7] text-[var(--t2)]">
        <T id="activity.project.note.desc" />
      </p>
      <div className="mt-2 grid gap-px">
        <Kv k={t('activity.project.note.overview')} v={t('activity.project.note.overview.desc')} wide />
        <Kv k={t('activity.project.note.wont')} v={t('activity.project.note.wont.desc')} ok wide />
      </div>
    </div>
  )
}

function SearchPanels() {
  return (
    <div className="mt-3 grid grid-cols-2 gap-2.5">
      <NotePanel title="能搜什么" tag="对普通成员" tone="g">
        <Kv k="名称" v="项目、会话、任务、站点、资源、Agent" />
        <Kv k="标识" v="commit SHA、deployment SHA、任务 ID、公开域名" />
        <Kv k="搜不到的" v="原始日志、IP、User-Agent、凭据内容" bad />
      </NotePanel>
      <NotePanel title="筛选六项" tag="项目单选，其余多选">
        <Kv k="项目 / 状态" v="项目可搜索单选；状态可多选。项目投影里不出现项目这一项" />
        <Kv k="类型" v="Lulu/Agent、开发、发布、静态网页、资源、项目、备忘录、团队、安全" />
        <Kv k="Actor / 时间" v="本人、Lulu、某 Agent、其他成员、系统 / 今天、7 天、30 天、自定义" />
      </NotePanel>
    </div>
  )
}

function OfflineNote() {
  return (
    <div className="mb-3 rounded-lg border border-[var(--wn)] bg-[var(--wn-s)] px-[13px] py-[11px]">
      <div className="mb-1 flex items-center gap-2">
        <span className="size-[7px] rounded-full bg-[var(--wn)]" />
        <span className="text-npc-section-sm font-semibold text-[var(--t1)]">
          <T id="activity.offline.note.title" />
        </span>
      </div>
      <p className="text-npc-body leading-[1.7] text-[var(--t2)]">
        <T id="activity.offline.note.desc" />
      </p>
    </div>
  )
}

function RecoverNote() {
  return (
    <div className="mb-3 rounded-lg border border-[var(--ok)] bg-[var(--ok-s)] px-[13px] py-[11px]">
      <div className="mb-1 flex items-center gap-2">
        <span className="size-[7px] rounded-full bg-[var(--ok)]" />
        <span className="text-npc-section-sm font-semibold text-[var(--t1)]">
          <T id="activity.recover.note.title" />
        </span>
      </div>
      <p className="text-npc-body leading-[1.7] text-[var(--t2)]">
        <T id="activity.recover.note.desc" />
      </p>
    </div>
  )
}

function ShaNote() {
  return (
    <div className="mt-3 rounded-lg border border-[var(--wn)] bg-[var(--wn-s)] px-[13px] py-[11px]">
      <div className="mb-1 flex items-center gap-2">
        <span className="size-[7px] rounded-full bg-[var(--wn)]" />
        <span className="text-npc-section-sm font-semibold text-[var(--t1)]">
          <T id="activity.sha.note.title" />
        </span>
      </div>
      <p className="text-npc-body leading-[1.7] text-[var(--t2)]">
        <T id="activity.sha.note.desc" />
      </p>
    </div>
  )
}

function HeldFailPanels() {
  return (
    <div className="mt-3 grid grid-cols-2 gap-2.5">
      <NotePanel title="平台挂起" tag="不算待办" tone="w">
        <Kv k="谁的问题" v="平台的，不是你的也不是 Agent 的" />
        <Kv k="会自己好吗" v="会 —— 修复后自动从断点接着跑" ok />
        <Kv k="要重新提交吗" v="不用。重复提交不加快，也不产生第二份" ok />
        <Kv k="进「要我处理」吗" v="不进 —— 你做不了什么" ok />
      </NotePanel>
      <NotePanel title="失败" tag="看情况" tone="r">
        <Kv k="稳定失败" v="重试多少次都一样，得改点什么" />
        <Kv k="偶发失败" v="平台自动重试，成功了你不会被打扰" ok />
        <Kv k="这一条" v="稳定失败 —— 版本对不上" bad />
        <Kv k="进「要我处理」吗" v="进 —— 有多个恢复方案要你选" warn />
      </NotePanel>
    </div>
  )
}

function NotePanel({
  title,
  tag,
  tone,
  children,
}: {
  title: string
  tag: string
  tone?: 'g' | 'w' | 'r'
  children: ReactNode
}) {
  return (
    <div className="rounded-lg border border-[var(--l2)] bg-[var(--bg-1)] px-[13px] py-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-npc-section-sm font-semibold text-[var(--t1)]">{title}</span>
        <span className="flex-1" />
        <span className="rounded-md px-2 py-0.5 text-npc-caption" style={pillStyle(tone ?? 'n')}>{tag}</span>
      </div>
      <div className="grid gap-px">{children}</div>
    </div>
  )
}

function Kv({ k, v, ok, bad, warn, wide }: { k: string; v: string; ok?: boolean; bad?: boolean; warn?: boolean; wide?: boolean }) {
  return (
    <div className="flex gap-2.5 border-b border-[var(--l1)] py-1">
      <span className={`${wide ? 'w-[104px]' : 'w-[92px]'} shrink-0 text-npc-caption text-[var(--t4)]`}>{k}</span>
      <span className={`min-w-0 flex-1 text-npc-body-sm ${ok ? 'text-[var(--ok-t)]' : bad ? 'text-[var(--bad-t)]' : warn ? 'text-[var(--wn-t)]' : 'text-[var(--t2)]'}`}>{v}</span>
    </div>
  )
}

export function useActivitySearchState(): ActivitySearch {
  const location = useLocation()
  return useMemo(() => parseActivitySearch(location.searchStr), [location.searchStr])
}
